import { Grid, useMediaQuery } from "@mui/material";
import Layout from "../../components/Layout";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import PageTitle from "../../components/PageTitle";
import Button from "../../components/Button";
import { useEffect, useState } from "react";
import Modal from "../../components/Modal";
import InputGroup from "../../components/FormComponent/InputGroup";
import Preview from "./components/Preview";
import { Icon } from "@iconify/react";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PopModal from "../../components/PopupModal/PopModal";
const EmailTemplate = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: "", body: "" });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [emailTemplateData, setEmailTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState({ id: "", title: "", body: "" });
  const [currentRow, setCurrentRow] = useState({});
  const [template, setTemplate] = useState({});

  /**
   * Toggles the modal for creating a new email template.
   */
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  /**
   * Fetches the email template by its ID and sets it in the state.
   * This function is called when a specific row is selected.
   * @param {number} id - The ID of the email template to fetch.
   */

  const fetchTemplateById = () => {
    httprequest
      .get(`/api/v1/email-temp/${currentRow?.id}`)
      .then((res) => {
        setTemplate(res.data.data);
      })
      .catch(() => {
      });
  };
  const handleBodyChange = (value) => {
    setNewTemplate({ ...newTemplate, body: value });
  };

  /**
   * Toggles the preview modal for viewing the email template.
   * This function is called when the user wants to preview an email template.
   */
  const togglePreviewModal = () => {
    setIsPreviewModalOpen(!isPreviewModalOpen);
  };

  /**
   * Toggles the edit modal for modifying an existing email template.
   * This function is called when the user wants to edit an email template.
   */
  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  /**
   * Toggles the delete modal for confirming the deletion of an email template.
   * This function is called when the user wants to delete an email template.
   */
  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTemplate({ ...newTemplate, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditTemplate({ ...editTemplate, [name]: value });
  };

  /**
   * Handles the click event for editing an email template.
   * Fetches the template details by its ID and opens the edit modal.
   * @param {*} row
   */
  const handleEditClick = async (row) => {
    setIsLoading(true);
    try {
      const response = await httprequest.get(`/api/v1/email-temp/${row.id}`);
      setEditTemplate({
        id: row.id,
        title: response.data.data.title,
        body: response.data.data.body
      });
      toggleEditModal();
    } catch (error) {
      toast.error("Failed to fetch template details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setCurrentRow(row);
    toggleDeleteModal();
  };

  /**
   * Handles the click event for viewing a preview of an email template.
   * Sets the current row data and opens the preview modal.
   * @param {*} row
   */
  const handleViewPreview = (row) => {
    setCurrentRow(row);
    togglePreviewModal();
  };

  /**
   * Fetches the email template data from the server.
   * This function is called when the component mounts to populate the email templates table.
   */
  const fetchEmailTemplateData = () => {
    httprequest
      .get("/api/v1/email-temp")
      .then((res) => {
        setEmailTemplateData(res.data.data);
      })
      .catch(() => {
      });
  };

  /**
   * Handles the submission of the edited email template.
   * Sends a PATCH request to update the template on the server.
   */
  const handleEditTemplate = () => {
    setIsLoading(true);
    httprequest
      .patch(`/api/v1/email-temp/${editTemplate.id}`, editTemplate)
      .then((res) => {
        toast.success(res?.data?.message);
        fetchEmailTemplateData();
        setIsEditModalOpen(false);
      })
      .catch((error) => {
        toast.error(error?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Handles the deletion of an email template.
   * Sends a DELETE request to remove the template from the server.
   */
  const handleDeleteTemplate = () => {
    setIsLoading(true);
    httprequest
      .delete(`/api/v1/email-temp/${currentRow?.id}`)
      .then((res) => {
        toast.success(res?.data?.message);
        fetchEmailTemplateData();
        setIsDeleteModalOpen(false);
      })
      .catch((error) => {
        toast.error(error?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Handles the creation of a new email template.
   * Sends a POST request to create the template on the server.
   */
  const handleCreateTemplate = () => {
    setIsLoading(true);
    httprequest
      .post("/api/v1/email-temp", newTemplate)
      .then((res) => {
        toast.success(res?.data?.message);
        fetchEmailTemplateData();
        setIsModalOpen(false);
      })
      .catch(() => {
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const columns = [
    {
      name: "Subject",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Subject</p>}
          {row.title}
        </>
      )
    },
    {
      name: "Created On",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Created On</p>}
          {row.created_at?.substring(0, 10)}
        </>
      )
    },
    {
      name: "Last Updated",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Last Updated</p>}
          {row.updated_at?.substring(0, 10)}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => handleViewPreview(row)}>
            <Icon icon="hugeicons:view" />
          </button>
          <button
            className="table-action-icon" onClick={() => handleEditClick(row)}
          >
            <Icon icon="lucide:edit" />
          </button>
          <button className="table-action-icon" onClick={() => handleDeleteClick(row)}>
            <div className="delete-icon"><Icon icon="mingcute:delete-2-line" /></div>
          </button>
        </div>
      )
    }
  ];

  const handleEditorDrop = (event) => {
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      // Prevent drop if it's a file (e.g., image)
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const handlePaste = (event) => {
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      // Check if any pasted item is an image
      if (items[i].type.indexOf("image") !== -1) {
        event.preventDefault(); // Prevent the paste

        return;
      }
    }
  };

  const Quillformats = ["background", "bold", "color", "font", "code", "italic", "link", "size",
    "strike", "script", "underline", "blockquote", "header", "indent", "list", "align", "direction", "code-block", "formula"
    // 'image'
    // 'video'
  ];

  useEffect(() => {
    fetchEmailTemplateData();
  }, []);
  useEffect(() => {
    fetchTemplateById();
  }, [currentRow]);

  return (
    <Layout children={
      <>
        <PageTitle
          title="Email Templates"
          subtitle="You can send bulk emails here."
          button={
            <div className="flex gap-10">
              <PopModal title="Email Template" videoUrl="https://youtu.be/xAISxGn0XwM" />
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                buttonName="Create Email Template"
                onClick={toggleModal}
              />
            </div>
          }
        />
        <div className="page-content">
          <Table
            data={emailTemplateData}
            columns={columns}
            pagination
            subHeader={true}
            subHeaderComponent={
              <TableSearch
                placeholder="Search email templates..."
              />
            }
          />
        </div>

        <Modal
          title="Create Email Template"
          subtitle="Save and embed the code for email templates"
          modalContent={
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="text"
                  label="Title"
                  inputName="title"
                  placeHolder="What is the title of mail."
                  required
                  value={newTemplate.title}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <p style={{ fontWeight: 500, fontSize: "14px" }} className="text-align-left no-margin-block">
                    Embedded Code
                    <span className="input-required">*</span>
                  </p>
                  <ReactQuill
                    theme="snow"
                    onDrop={handleEditorDrop}
                    value={newTemplate.body}
                    onChange={handleBodyChange}
                    onPaste={handlePaste}
                    formats={Quillformats}
                    modules={{
                      toolbar: [
                        [{ "header": [1, 2, false] }],
                        ["bold", "italic", "underline"],
                        [{ "list": "ordered" }, { "list": "bullet" }],
                        ["clean"] // Remove the image button
                      ]
                    }}
                    style={{
                      borderRadius: 30,
                      textAlign: "left",
                      "& qlContainer": { height: "150px" }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          }
          modalFooter={
            <div>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Create Template"
                onClick={handleCreateTemplate}
                isLoading={isLoading}
              />
            </div>
          }
          isopen={isModalOpen}
          closeModal={toggleModal}
        />
        <Modal
          title="Preview Email Template"
          subtitle="Here is a preview of your email template"
          modalContent={
            <>
              <Preview Title={template.title} Body={template.body} />
            </>
          }
          isopen={isPreviewModalOpen}
          closeModal={togglePreviewModal}
        />

        <Modal
          title="Edit Email Template"
          subtitle="Modify the details of the email template"
          modalContent={
            <Grid container spacing={2}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <InputGroup
                  inputType="text"
                  label="Title"
                  inputName="title"
                  placeHolder="What is the title of the email."
                  required
                  value={editTemplate.title}
                  onChange={handleEditInputChange}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <p style={{ fontWeight: 500, fontSize: "14px" }} className="text-align-left no-margin-block">
                    Embedded Code
                  <span className="input-required">*</span>
                </p>
                <ReactQuill
                  theme="snow"
                  onDrop={handleEditorDrop}
                  value={editTemplate.body || ""}
                  onChange={(value) => setEditTemplate({ ...editTemplate, body: value })}
                  onPaste={handlePaste}
                  formats={Quillformats}
                  modules={{
                    toolbar: [
                      [{ "header": [1, 2, false] }],
                      ["bold", "italic", "underline"],
                      [{ "list": "ordered" }, { "list": "bullet" }],
                      ["clean"]
                    ]
                  }}
                  style={{
                    borderRadius: 30,
                    textAlign: "left",
                    "& qlContainer": { height: "150px" }
                  }}
                />
              </Grid>
            </Grid>
          }
          modalFooter={
            <div>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName={isLoading ? "Updating..." : "Update Template"}
                onClick={handleEditTemplate}
                disabled={isLoading}
              />
            </div>
          }
          isopen={isEditModalOpen}
          closeModal={toggleEditModal}
        />

        <Modal
          title="Delete Email Template"
          subtitle="Are you sure you want to delete this email template?"
          modalFooter={
            <div>
              <Button
                variant="danger"
                buttonSize="full"
                color="btndefault"
                buttonName={isLoading ? "Deleting..." : "Delete Template"}
                onClick={handleDeleteTemplate}
                disabled={isLoading}
              />
            </div>
          }
          isopen={isDeleteModalOpen}
          closeModal={toggleDeleteModal}
        />

        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default EmailTemplate;
